package org.hs_soft.runmynesto.pages.home.sub_page.dashboard.component.dashboard_chart.component.chart.component

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.NoLiveLiterals
import kotlinx.browser.document
import org.hs_soft.runmynesto.domain.config.Dimen
import org.hs_soft.runmynesto.domain.model.general.WindowSizeModeEnum
import org.hs_soft.runmynesto.domain.util.formatToTwoDecimals
import org.hs_soft.runmynesto.domain.util.helper.LanguageManager
import org.hs_soft.runmynesto.domain.util.jsObject
import org.hs_soft.runmynesto.modules.Chart
import org.hs_soft.runmynesto.modules.React
import org.hs_soft.runmynesto.modules.ReactDom
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.paddingBottom
import org.jetbrains.compose.web.css.paddingRight
import org.jetbrains.compose.web.css.paddingTop
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import kotlin.js.Json
import kotlin.js.json


val TODAY=  LanguageManager.getCurrentStrings().today

@Composable
fun AreaChart(
    windowSizeMode: WindowSizeModeEnum,
    id: String,
    firstFillColor: String,
    firstStrokeColor: String,
    secondFillColor: String,
    secondStrokeColor: String,
    firstChartTitle:String,
    secondChartTitle:String,
    mainData: List<Double?>?,
    comparedData: List<Double?>?,
    interval: List<String?>?,
) {

    Div(attrs = {
        id(id)
        style {
            width(100.percent)
            height(100.percent)
            minHeight(
               when(windowSizeMode){
                   WindowSizeModeEnum.PHONE-> Dimen.chartMobileHeight
                   WindowSizeModeEnum.TABLET->Dimen.chartTabletHeight
                   WindowSizeModeEnum.Desktop->Dimen.chartWindowsHeight
                }

            )
            paddingTop(Dimen.largePadding)
            paddingRight(Dimen.mediumPadding)
            paddingBottom(Dimen.largePadding)
        }
    }) {
        DisposableEffect(
            mainData,
            comparedData,
        ) {
            val container = document.getElementById(id) as HTMLElement
            createChart(
                windowSizeMode=windowSizeMode,
                mainData=mainData,
                comparedData=comparedData,
                interval=interval,
                container=container,
                firstFillColor = firstFillColor,
                firstChartTitle = firstChartTitle,
                firstStrokeColor = firstStrokeColor,
                secondFillColor = secondFillColor,
                secondChartTitle = secondChartTitle,
                secondStrokeColor = secondStrokeColor,
            )
            onDispose { }
        }
    }
}



private fun createChart(
    windowSizeMode: WindowSizeModeEnum,
    container: HTMLElement,
    firstFillColor: String,
    firstStrokeColor: String,
    firstChartTitle: String,
    secondFillColor: String,
    secondStrokeColor: String,
    secondChartTitle: String,
    mainData: List<Double?>?,
    comparedData: List<Double?>?,
    interval: List<String?>?,
) {
    val jsonList: Array<Json> = mainData?.mapIndexed { index, value ->

        val intervalLabel = interval?.getOrNull(index)
            ?.split("-")?.firstOrNull() ?: "N/A"

        val secondChartValue = comparedData?.getOrNull(index)?.let {
            if (it == 0.0) "0.0" else formatToTwoDecimals(it)
        } ?: "0.0"

        if (secondChartTitle==firstChartTitle){
            json(
                "name" to intervalLabel,
                TODAY to (formatToTwoDecimals(value)),
                "amt" to 2400
            )
        }else{
            json(
                "name" to intervalLabel,
                TODAY to (formatToTwoDecimals(value)),
                secondChartTitle to secondChartValue,
                "amt" to 2400
            )
        }


    }?.toTypedArray() ?: emptyArray()


    renderChart(
        windowSizeMode=windowSizeMode,
        container = container,
        data = jsonList,
        firstFillColor = firstFillColor,
        firstStrokeColor = firstStrokeColor,
        secondFillColor =secondFillColor ,
        secondStrokeColor =secondStrokeColor ,
        secondChartTitle=secondChartTitle,

    )


}


@NoLiveLiterals
private fun renderChart(
    windowSizeMode: WindowSizeModeEnum,
    container: HTMLElement,
    data: Array<dynamic>,
    firstFillColor: String,
    firstStrokeColor: String,
    secondFillColor: String,
    secondStrokeColor: String,
    secondChartTitle: String,
) {
    val rightPadding = when(windowSizeMode){
        WindowSizeModeEnum.Desktop -> 0
        WindowSizeModeEnum.TABLET -> 0
        WindowSizeModeEnum.PHONE -> Dimen.largePadding.value.toInt()
    }
    val leftMargin=-25
    val topBottomPadding = Dimen.normalPadding.value.toInt()

    val maxValue = data.maxOf { item ->
        val value1 = (item[TODAY] as? String)?.toDoubleOrNull() ?: 0.0
        val value2 = (item[secondChartTitle] as? String)?.toDoubleOrNull() ?: 0.0
        maxOf(value1, value2)
    }

    val yAxisMax = maxValue * 1.1

    val root = ReactDom.createRoot(container)
    root.render(
        React.createElement(
            Chart.ResponsiveContainer, jsObject<Any> {
                width = "100%"
                height = "100%"
            },
            React.createElement(
                Chart.AreaChart, jsObject<Any> {
                    this.data = data
                    margin = jsObject<Any> {
                        top = topBottomPadding
                        right = rightPadding
                        left = leftMargin
                        bottom = topBottomPadding
                    }
                },
                React.createElement(Chart.CartesianGrid, jsObject<Any> {
                    strokeDasharray = "0 0"
                }),
                React.createElement(Chart.XAxis, jsObject<Any> {
                    dataKey = "name"
                }),
                React.createElement(Chart.YAxis, jsObject<Any> {
                    domain = arrayOf(0, yAxisMax)
                    allowDataOverflow = false
                    scale = "linear"
                }),
                React.createElement(Chart.Tooltip, null),
                React.createElement(Chart.Area, jsObject<Any> {
                    type = "linear"
                    dataKey = TODAY
                    stroke = firstStrokeColor
                    fill = firstFillColor
                    activeDot = jsObject<Any> {
                        r = 5
                        stroke = firstStrokeColor
                        fill = firstStrokeColor
                        strokeWidth = 2
                    }
                    dot = jsObject<Any> {
                        r = 2
                        stroke = firstStrokeColor
                        fill = firstStrokeColor
                        strokeWidth = 3
                    }
                }),
                React.createElement(Chart.Area, jsObject<Any> {
                    type = "linear"
                    dataKey = secondChartTitle
                    stroke = secondStrokeColor
                    fill = secondFillColor
                    activeDot = jsObject<Any> {
                        r = 5
                        stroke = secondStrokeColor
                        fill = secondStrokeColor
                        strokeWidth = 2
                    }
                    dot = jsObject<Any> {
                        r = 2
                        stroke = secondStrokeColor
                        fill = secondStrokeColor
                        strokeWidth = 3
                    }
                })
            )
        )
    )
}